import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import cn from "classnames";
import stylesCommon from "../common.module.scss";
import styles from "../RegistrationPageNew/page.module.scss";
import CustomInput from "../../custom/custom-input/CustomInput";
import CustomButton from "../../custom/custom-button/CustomButton";
import CustomSwitch from "../../custom/custom-switch/CustomSwitch";
import CloseIcon from "../../shared/icons/CloseIcon";
import { User } from "../../../services";
import { toast } from "react-toastify";
import { requestErrorDisplay } from "../../../utils";

const BIN_LENGTH = 12;

const validatePhoneNumber = (phoneNumber) => {
  const chinaPattern = /^\+86\s\d{3}\s\d{4}\s\d{4}$|^86\d{11}$/;
  return chinaPattern.test(phoneNumber);
};

const validateUserName = (userName, t) => {
  if (!userName || userName.trim() === "") {
    return { isValid: false, errorMessage: t("registrationForm.validation.nameRequired") };
  }

  const nameParts = userName.trim().split(/\s+/);

  if (nameParts.length < 1) {
    return { isValid: false, errorMessage: t("registrationForm.validation.fullNameRequired") };
  }

  const namePattern = /^[\p{L}]+(-[\p{L}]+)*$/u;

  for (const part of nameParts) {
    if (!namePattern.test(part)) {
      return {
        isValid: false,
        errorMessage: t("registrationForm.validation.nameFormat"),
      };
    }
  }

  return { isValid: true, errorMessage: null };
};

const validateCompanyName = (companyName) => {
  const namePattern = /^[A-Za-zА-Яа-я]/;
  return namePattern.test(companyName);
};

const RegistrationForm = ({ data, updateStep, setData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [userName, setUserName] = useState(
    `${data.surname || ""} ${data.name || ""} ${data.patronymic || ""}`.trim(),
  );
  const [phoneNumber, setPhoneNumber] = useState(data?.phoneNumber || "");
  const [bin, setBin] = useState(data?.bin || "");
  const [companyName, setCompanyName] = useState(data?.companyName || "");
  const [isChecked, setIsChecked] = useState(data?.isChecked || false);
  const [isCheckedPhone, setIsCheckedPhone] = useState(true);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [patronymic, setPatronymic] = useState("");

  const [errorTitleName, setErrorTitleName] = useState();
  const [errorTitlePhone, setErrorTitlePhone] = useState();
  const [errorTitleBin, setErrorTitleBin] = useState();
  const [errorTitleCompanyName, setErrorTitleCompanyName] = useState();
  const [errorIsChecked, setErrorIsChecked] = useState();

  useEffect(() => {
    if (data.phoneNumber) setPhoneNumber(data.phoneNumber);
    if (data.bin) setBin(data.bin);
    if (data.companyName) setCompanyName(data.companyName);
    if (data.surname || data.name || data.patronymic) {
      const fullName = `${data.surname || ""} ${data.name || ""} ${data.patronymic || ""}`.trim();
      setUserName(fullName);
    }
  }, [data]);

  useEffect(() => {
    if (!userName) return;
    const validationResult = validateUserName(userName, t);

    if (validationResult.isValid) {
      const names = userName.trim().split(/\s+/);
      setName(names[0] || "");
      setSurname(names[1] || "");
      setPatronymic(names[2] || "");
      setErrorTitleName(null);
    } else {
      setErrorTitleName(validationResult.errorMessage);
    }
  }, [userName]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has("userName")) {
      setUserName(urlParams.get("userName"));
    }
    if (urlParams.has("phoneNumber")) {
      setPhoneNumber(urlParams.get("phoneNumber"));
    }
    if (urlParams.has("bin")) {
      setBin(urlParams.get("bin"));
    }
    if (urlParams.has("companyName")) {
      setCompanyName(urlParams.get("companyName"));
    }
  }, []);

  const validateData = async () => {
    setErrorTitleName(null);
    setErrorTitlePhone(null);
    setErrorTitleBin(null);
    setErrorTitleCompanyName(null);
    setErrorIsChecked(null);

    if (!userName) {
      setErrorTitleName(t("registrationForm.validation.nameRequired"));
      return;
    }
    const nameValidation = validateUserName(userName, t);
    if (!nameValidation.isValid) {
      setErrorTitleName(nameValidation.errorMessage);
      return;
    }
    if (!validatePhoneNumber(phoneNumber)) {
      setErrorTitlePhone(t("registrationForm.validation.invalidPhoneFormat"));
      return;
    }

    if (!bin) {
      setErrorTitleBin(t("registrationSurveyUserFirst.validation.invalidBinLength"));
      return;
    }

    if (!validateCompanyName(companyName)) {
      setErrorTitleCompanyName(t("registrationSurveyUserFirst.validation.companyNameRequired"));
      return;
    }

    if (!isChecked) {
      toast.error(t("registrationForm.toast.termsRequired"));
      setErrorIsChecked(t("registrationForm.validation.termsRequired"));
      return;
    }

    let formattedPhoneNumber = phoneNumber.replace(/\D/g, "");
    const isPhoneValid = await checkPhone(formattedPhoneNumber);
    if (!isPhoneValid) return;

    const isBinValid = await checkBin(bin);
    if (!isBinValid) return;

    if (isChecked) {
      setData({
        ...data,
        name: name,
        surname: surname,
        patronymic: patronymic,
        phoneNumber: formattedPhoneNumber,
        bin: bin,
        companyName: companyName,
        isChecked: isChecked,
      });
      nextStep();
    }
  };

  const checkPhone = async (phoneNumber) => {
    try {
      const response = await User.check("phoneNumber", phoneNumber);
      if (response.status === 200 && response.data.isExists) {
        toast.error(t("registrationForm.toast.phoneExists"));
        setErrorTitlePhone(t("registrationForm.toast.phoneExists"));
        setIsCheckedPhone(false);
        return false;
      }
      setIsCheckedPhone(true);
      return true;
    } catch (error) {
      requestErrorDisplay(error, t("registrationForm.toast.genericError"));
      return false;
    }
  };

  const checkBin = async (bin) => {
    try {
      const response = await User.check("bin", bin);
      if (response.data.isExists) {
        toast.error(t("registrationSurveyUserFirst.validation.binExists"));
        setErrorTitleBin(t("registrationSurveyUserFirst.validation.binExists"));
        return false;
      }
      return true;
    } catch (error) {
      requestErrorDisplay(error, t("registrationForm.toast.genericError"));
      return false;
    }
  };

  const handleToggle = (newState) => {
    setIsChecked(newState);
  };

  const nextStep = () => {
    if (!isCheckedPhone) {
      return;
    } else updateStep(2);
  };

  // Определяем placeholder для телефона в зависимости от региона
  const getPhonePlaceholder = () => {
    return "+86 138 0013 8000";
  };

  return (
    <>
      <form className={styles.card_form}>
        <CustomInput
          placeholder={t("registrationForm.name.placeholder")}
          labelText={t("registrationForm.name.label")}
          inputType="text"
          onChangeValue={setUserName}
          value={userName}
          errorText={errorTitleName}
        />
        <CustomInput
          placeholder={getPhonePlaceholder()}
          labelText={t("registrationForm.phone.label")}
          inputType="text"
          onChangeValue={setPhoneNumber}
          value={phoneNumber}
          errorText={errorTitlePhone}
          isPhoneCN
        />
        <CustomInput
          placeholder="123456789012"
          labelText={t("registrationSurveyUserFirst.bin.label")}
          inputType="text"
          onChangeValue={setBin}
          value={bin}
          errorText={errorTitleBin}
          maxLength={BIN_LENGTH}
          isBin
        />
        <CustomInput
          placeholder={t("registrationSurveyUserFirst.companyName.placeholder")}
          labelText={t("registrationSurveyUserFirst.companyName.label")}
          inputType="text"
          onChangeValue={setCompanyName}
          value={companyName}
          errorText={errorTitleCompanyName}
        />

        <div className={styles.card_offer}>
          <div className={styles.card_text}>
            <span>
              {t("registrationForm.terms.prefix")}
              <a href="https://saas.biny.co//offer-agreement.pdf" target="_blank" rel="noopener noreferrer">
                {t("registrationForm.terms.link")}
              </a>
            </span>
            <CustomSwitch onToggle={handleToggle} defaultValue={isChecked} />
          </div>
          {errorIsChecked && <div className={styles.errorText}>{errorIsChecked}</div>}
        </div>
      </form>

      <div className={styles.btns}>
        <CustomButton
          isFall
          text={t("registrationForm.buttons.continue")}
          onClick={validateData}
          isLoading={isLoading}
        />
        <div className={styles.btns_text}>
          <div className={styles.btns_before}></div>
          <div className={styles.btns_textDashes}>{t("registrationForm.login.prompt")}</div>
          <div className={styles.btns_before}></div>
        </div>
        <CustomButton
          isFall
          text={t("registrationForm.buttons.login")}
          isWhite={true}
          onClick={() => navigate("/login")}
        />
      </div>
    </>
  );
};

export default RegistrationForm;
